import {
  authoritativeSportsbookHistoryRow,
  invalidateSportsbookHistoryRead,
  patchSportsbookHistoryCursorState,
  patchSportsbookHistoryPageToken,
  patchSportsbookHistoryRows,
} from './sportsbookHistoryAuthoritativeRow.query.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { requestSportsbookHistoryPage, getSportsbookHistoryErrorMessage } from '../api/sportsbookHistoryRequest.api.js';
import {
  advanceSportsbookHistoryCursor,
  createSportsbookHistoryCursorContext,
  resolveSportsbookHistoryCursorState,
  retreatSportsbookHistoryCursor,
  selectSportsbookHistoryCursorPage,
  sportsbookHistoryCursorPageNumber,
} from '../cursor/sportsbookHistoryCursor.cursor.js';
import { fetchBoundedVisibleSportsbookHistoryPage } from '../cursor/sportsbookHistoryHiddenFill.cursor.js';
import {
  mergeOptimisticSportsbookHistoryRows,
  reconcileOptimisticSportsbookHistoryRows,
  storeOptimisticSportsbookHistoryRow,
} from '../optimistic/sportsbookHistoryOptimisticRows.optimistic.js';
import {
  SPORTSBOOK_HISTORY_OPTIMISTIC_EVENT,
  SPORTSBOOK_HISTORY_PAGE_LIMIT,
} from '../policies/sportsbookHistory.policy.js';
import {
  buildSportsbookHistoryQuery,
  sportsbookHistoryRowMatchesQuery,
} from './sportsbookHistoryQueryParams.query.js';

export const useSportsbookHistoryQuery = ({
  service,
  dateRange,
  customStartDate,
  customEndDate,
  status,
  category,
  hiddenHistoryIds,
}) => {
  const query = useMemo(() => buildSportsbookHistoryQuery({
    dateRange,
    customStartDate,
    customEndDate,
    status,
    category,
  }), [category, customEndDate, customStartDate, dateRange, status]);
  const queryKey = useMemo(() => JSON.stringify(query), [query]);
  const [cursorContext, setCursorContext] = useState(() => createSportsbookHistoryCursorContext(queryKey));
  const cursorState = useMemo(
    () => resolveSportsbookHistoryCursorState(cursorContext, queryKey),
    [cursorContext, queryKey],
  );
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState({ snapshot_at: '', next_cursor: null, has_more: false });
  const [nextPageToken, setNextPageToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refreshToken, setRefreshToken] = useState(0);
  const [optimisticRevision, setOptimisticRevision] = useState(0);
  const requestRef = useRef({ controller: null, generation: 0 });
  const previousQueryKeyRef = useRef(queryKey);

  useEffect(() => {
    if (previousQueryKeyRef.current === queryKey) return;
    previousQueryKeyRef.current = queryKey;
    setCursorContext({ queryKey, state: cursorState });
    setRows([]);
    setPage({ snapshot_at: '', next_cursor: null, has_more: false });
    setNextPageToken(null);
    setError('');
  }, [cursorState, queryKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleOptimisticRow = (event) => {
      const storedRow = storeOptimisticSportsbookHistoryRow(event?.detail?.historyRow);
      if (!storedRow) return;
      if (
        cursorState.visitedPageTokens.length === 0
        && !hiddenHistoryIds.has(storedRow.history_id)
        && sportsbookHistoryRowMatchesQuery(storedRow, query)
      ) {
        setRows((currentRows) => mergeOptimisticSportsbookHistoryRows([storedRow], currentRows));
      }
      setOptimisticRevision((value) => value + 1);
    };
    window.addEventListener(SPORTSBOOK_HISTORY_OPTIMISTIC_EVENT, handleOptimisticRow);
    return () => window.removeEventListener(SPORTSBOOK_HISTORY_OPTIMISTIC_EVENT, handleOptimisticRow);
  }, [cursorState.visitedPageTokens.length, hiddenHistoryIds, query, queryKey]);

  useEffect(() => {
    const controller = new AbortController();
    requestRef.current.controller?.abort();
    const generation = requestRef.current.generation + 1;
    requestRef.current = { controller, generation };
    setNextPageToken(null);
    setLoading(true);
    setError('');

    fetchBoundedVisibleSportsbookHistoryPage({
      fetchPage: (params, signal) => requestSportsbookHistoryPage({ service, params, signal }),
      query,
      pageToken: cursorState.currentPageToken,
      hiddenHistoryIds,
      signal: controller.signal,
      visibleLimit: SPORTSBOOK_HISTORY_PAGE_LIMIT,
    }).then((result) => {
      if (controller.signal.aborted || requestRef.current.generation !== generation) return;
      const remainingOptimisticRows = reconcileOptimisticSportsbookHistoryRows(result.rows);
      const visibleOptimisticRows = cursorState.visitedPageTokens.length === 0
        ? remainingOptimisticRows.filter((row) => (
          !hiddenHistoryIds.has(row.history_id) && sportsbookHistoryRowMatchesQuery(row, query)
        ))
        : [];
      setRows(mergeOptimisticSportsbookHistoryRows(visibleOptimisticRows, result.rows));
      setPage(result.page);
      setNextPageToken(result.nextPageToken);
    }).catch((requestError) => {
      if (controller.signal.aborted || requestError?.name === 'AbortError' || requestRef.current.generation !== generation) return;
      setError(getSportsbookHistoryErrorMessage(requestError, '베팅내역을 불러오지 못했습니다.'));
    }).finally(() => {
      if (!controller.signal.aborted && requestRef.current.generation === generation) setLoading(false);
    });

    return () => controller.abort();
  }, [cursorState, hiddenHistoryIds, optimisticRevision, query, queryKey, refreshToken, service]);

  const refresh = useCallback(() => setRefreshToken((value) => value + 1), []);
  const applyAuthoritativeDetail = useCallback((detail) => {
    const confirmed = authoritativeSportsbookHistoryRow(detail);
    invalidateSportsbookHistoryRead(requestRef);
    reconcileOptimisticSportsbookHistoryRows([confirmed]);
    setRows((current) => patchSportsbookHistoryRows(current, confirmed, query));
    setNextPageToken((current) => patchSportsbookHistoryPageToken(current, confirmed, query));
    setCursorContext((current) => ({
      queryKey,
      state: patchSportsbookHistoryCursorState(
        resolveSportsbookHistoryCursorState(current, queryKey), confirmed, query,
      ),
    }));
    setRefreshToken((value) => value + 1);
  }, [query, queryKey]);

  const goNext = useCallback(() => {
    if (!nextPageToken || cursorContext.queryKey !== queryKey) return;
    setCursorContext((current) => {
      const state = resolveSportsbookHistoryCursorState(current, queryKey);
      return { queryKey, state: advanceSportsbookHistoryCursor(state, nextPageToken) };
    });
  }, [cursorContext.queryKey, nextPageToken, queryKey]);
  const goBack = useCallback(() => {
    setCursorContext((current) => {
      const state = resolveSportsbookHistoryCursorState(current, queryKey);
      const nextState = retreatSportsbookHistoryCursor(state);
      return nextState === state ? current : { queryKey, state: nextState };
    });
  }, [queryKey]);
  const goToPage = useCallback((pageNumber) => {
    setCursorContext((current) => {
      const state = resolveSportsbookHistoryCursorState(current, queryKey);
      const nextState = selectSportsbookHistoryCursorPage(state, pageNumber, nextPageToken);
      return nextState === state ? current : { queryKey, state: nextState };
    });
  }, [nextPageToken, queryKey]);

  return {
    rows,
    page,
    loading,
    error,
    refresh,
    applyAuthoritativeDetail,
    goNext,
    goBack,
    goToPage,
    canGoNext: cursorContext.queryKey === queryKey && nextPageToken !== null,
    canGoBack: cursorState.visitedPageTokens.length > 0,
    pageNumber: sportsbookHistoryCursorPageNumber(cursorState),
    pageLimit: SPORTSBOOK_HISTORY_PAGE_LIMIT,
  };
};
